import { ZocNode } from "@/core/battle/ai/types/ai.types";
import { CombatCalculator } from "@/core/battle/CombatCalculator";
import { EntityRegistry } from "@/core/battle/EntityRegistry";
import ManagerRegistry from "@/core/battle/ManagerRegistry";
import { PathCalculator } from "@/core/battle/PathCalculator";
import { BattleUnit, Hex, Skill } from "@/core/types/battle.types";
import { idToCoords, isLineBlocked } from "@/core/utils/hex.utils";

interface DamageKnapsack {
    tables: Map<number, number[]>;
    skillByRange: Map<number, Skill>;
    /** Фіксований hitChance для range <= 2 (distMod завжди 0).
     *  null = рахувати per dist в evaluateSingleHex */
    hitChanceByRange: Map<number, number | null>;
    attackSkill: number;
    maxRange: number;
    step: number;
}

interface ActiveEnemy {
    instance: BattleUnit;
    damageTable: DamageKnapsack;
    reachableMap: Map<string, number>;
}

export class ThreatMapCalculator {

    /**
     * Головний метод
     */
    public static calculate(
        unitReachableNodes: Map<string, number>,
        enemies: BattleUnit[],
        defender: BattleUnit,
        battleMap: Map<string, Hex>,
        zocMap: Map<string, ZocNode>,
    ): Map<string, number> {
        const threatMap = new Map<string, number>();
        const registry = ManagerRegistry.getInstance().entityRegistry;

        const activeEnemies: ActiveEnemy[] = enemies.map(enemy => ({
            instance: enemy,
            damageTable: this.precomputeDamageKnapsack(enemy, defender),
            reachableMap: PathCalculator.getReachableNodes(
                { q: enemy.q, r: enemy.r },
                { currentStamina: enemy.stats.stamina, speed: enemy.stats.speed },
                battleMap,
                zocMap
            ).reachableMap
        }));

        for (const defenderHex of unitReachableNodes) {
            const threat = this.evaluateSingleHex(defenderHex, activeEnemies, defender, battleMap, registry);
            if (threat > 0) {
                threatMap.set(defenderHex[0], threat);
            }
        }

        return threatMap;
    }

    /**
     * Оцінка одного гекса
     */
    private static evaluateSingleHex(
        defenderHex: [string, number],
        enemies: ActiveEnemy[],
        defender: BattleUnit,
        battleMap: Map<string, Hex>,
        registry: EntityRegistry
    ): number {

        const enemyOptions: Array<{
            enemy: ActiveEnemy;
            options: Array<{ slot: string; damage: number }>;
        }> = [];

        const pos: { q: number; r: number; } = registry.getHex(defenderHex[0])?.data ?? idToCoords(defenderHex[0]);

        for (const enemyData of enemies) {
            const options: Array<{ slot: string; damage: number }> = [];
            const { maxRange, skillByRange, hitChanceByRange, attackSkill, step } = enemyData.damageTable;

            const potentialAttackSlots = PathCalculator.getRadius(
                pos,
                maxRange,
                battleMap,
                false,
                true
            );

            potentialAttackSlots.forEach((dist, slotId) => {
                const moveCost = enemyData.reachableMap.get(slotId);
                if (moveCost === undefined) return;

                // LOS: мілі-атаки (dist 1) не блокуються
                if (dist > 1) {
                    const attackerPos: { q: number; r: number; } = registry.getHex(slotId)?.data ?? idToCoords(slotId);
                    if (isLineBlocked(attackerPos, pos, battleMap)) return;
                }

                const staminaLeft = enemyData.instance.stats.stamina - moveCost;
                if (staminaLeft < 0) return;

                const damageTable = enemyData.damageTable.tables.get(dist);
                if (!damageTable) return;

                const rawDamage = this.getMaxDamageForStamina(damageTable, staminaLeft, step);
                if (rawDamage <= 0) return;

                // range <= 2: hitChance фіксований з precompute (distMod завжди 0)
                // range > 2: рахуємо per dist — getDistMod штрафує субоптимальну дистанцію
                const cached = hitChanceByRange.get(dist);
                const hitChance = (cached !== null && cached !== undefined)
                    ? cached
                    : (() => {
                        const skill = skillByRange.get(dist);
                        return skill
                            ? CombatCalculator.getHitChance(enemyData.instance, attackSkill, defender, skill, dist) / 100
                            : 1;
                    })();

                const expectedDamage = rawDamage * hitChance;
                if (expectedDamage > 0) {
                    options.push({ slot: slotId, damage: expectedDamage });
                }
            });

            if (options.length > 0) {
                options.sort((a, b) => b.damage - a.damage);
                enemyOptions.push({ enemy: enemyData, options });
            }
        }

        // Constraint-based sorting
        enemyOptions.sort((a, b) => {
            const optA = a.options[0];
            const optB = b.options[0];
            if (!optA || !optB) return 0;
            if (a.options.length !== b.options.length) {
                return a.options.length - b.options.length;
            }
            return optB.damage - optA.damage;
        });

        // Resource locking
        let totalThreat = 0;
        const occupiedSlots = new Set<string>();

        for (const entry of enemyOptions) {
            const bestMove = entry.options.find(opt => !occupiedSlots.has(opt.slot));
            if (bestMove) {
                totalThreat += bestMove.damage;
                occupiedSlots.add(bestMove.slot);
            }
        }

        return totalThreat;
    }

    /**
     * Knapsack DP з врахуванням броні defender.
     * - skillByRange: найкращий скіл per range для getHitChance
     * - hitChanceByRange: фіксований коефіцієнт для range <= 2, null для range > 2
     */
    private static precomputeDamageKnapsack(enemy: BattleUnit, defender: BattleUnit): DamageKnapsack {
        const STEP = 5;
        const maxStaminaSteps = Math.floor(enemy.stats.stamina / STEP);
        const damageTablesByRange = new Map<number, number[]>();
        const skillByRange = new Map<number, Skill>();
        const hitChanceByRange = new Map<number, number | null>();
        const attackSkill = enemy.skills.some(s => s.range > 2)
            ? (enemy.stats.rangedSkill ?? enemy.stats.meleeSkill ?? 0)
            : (enemy.stats.meleeSkill ?? 0);

        const skillsByRange = enemy.skills.reduce((acc, skill) => {
            if (!acc[skill.range]) acc[skill.range] = [];
            const stepCost = Math.ceil(skill.cost / STEP);
            const rawDamage = CombatCalculator.calculateRawDamage(
                enemy,
                defender,
                skill.powerMult || 1.0,
                enemy.stats.armorPenetration + (skill.armorPenBonus || 0),
                true
            );
            acc[skill.range]!.push({ stepCost, damage: rawDamage, skill });
            return acc;
        }, {} as Record<number, { stepCost: number; damage: number; skill: Skill }[]>);

        // Зберігаємо найкращий скіл per range + hitChance для range <= 2
        for (const [rangeStr, skills] of Object.entries(skillsByRange)) {
            const range = Number(rangeStr);
            const best = skills.reduce((a, b) => a.damage > b.damage ? a : b);
            skillByRange.set(range, best.skill);

            if (range <= 2) {
                // distMod завжди 0 для мілі/короткого range — рахуємо один раз
                hitChanceByRange.set(
                    range,
                    CombatCalculator.getHitChance(enemy, attackSkill, defender, best.skill, range) / 100
                );
            } else {
                // рахуємо per dist в evaluateSingleHex
                hitChanceByRange.set(range, null);
            }
        }

        const sortedRanges = Object.keys(skillsByRange).map(Number).sort((a, b) => b - a);
        if (sortedRanges.length === 0) {
            return { tables: damageTablesByRange, skillByRange, hitChanceByRange, attackSkill, maxRange: 0, step: STEP };
        }

        const maxRange = sortedRanges[0]!;
        let lastDp = new Array(maxStaminaSteps + 1).fill(0);

        for (let dist: number = maxRange; dist >= 1; dist--) {
            if (skillsByRange[dist]) {
                const nextDp = [...lastDp];
                for (const s of skillsByRange[dist]!) {
                    for (let i = s.stepCost; i <= maxStaminaSteps; i++) {
                        const potentialDmg = nextDp[i - s.stepCost]! + s.damage;
                        if (potentialDmg > nextDp[i]!) {
                            nextDp[i] = potentialDmg;
                        }
                    }
                }
                for (let i = 1; i <= maxStaminaSteps; i++) {
                    nextDp[i] = Math.max(nextDp[i]!, nextDp[i - 1]!);
                }
                lastDp = nextDp;
            }
            damageTablesByRange.set(dist, lastDp);
        }

        return { tables: damageTablesByRange, skillByRange, hitChanceByRange, attackSkill, maxRange, step: STEP };
    }

    private static getMaxDamageForStamina(damageTable: number[], staminaLeft: number, step = 5): number {
        const s = Math.floor(staminaLeft / step);
        if (s < 0) return 0;
        if (s >= damageTable.length) return damageTable[damageTable.length - 1] ?? 0;
        return damageTable[s] ?? 0;
    }
}